import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getAttendanceList,
  getReimbursementCategories,
  getReimbursementSpendLimit,
} from "_controllers/officer";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import authAxios from "setups/axios";
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";

const Main = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [spendLimit, setSpendLimit] = useState([]);

  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState("");
  const [long, setLong] = useState("");

  const [zone, setZone] = useState("");
  const [region, setRegion] = useState("");
  const [area, setArea] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");

  const userType = [
    { value: "SO", name: "Sales Officer" },
    { value: "AO", name: "Area Manager" },
    { value: "RO", name: "Regional Manager" },
    { value: "ZO", name: "Zonal Manager" },
  ];

  useEffect(async () => {
    const attendance = await getAttendanceList({
      currentPage,
      search,
      zone,
      region,
      area,
    });
    setData(attendance?.data);
    setMessage(attendance?.message);
    setLoading(true);
    setTotalPages(attendance?.totalPage ?? 1);
  }, [message, currentPage, search]);

  useEffect(async () => {
    const categoryData = await getReimbursementCategories();
    setCategories(categoryData?.data);
    setLoading(true);
  }, [message]);

  useEffect(async () => {
    const categoryData = await getReimbursementSpendLimit();
    setSpendLimit(categoryData?.data);
    setLoading(true);
  }, [message]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const [formData, setFormData] = useState([]);

  const handleAmountChange = (categoryId, subCategoryId, amount) => {
    // Create a copy of the categories state
    const updatedCategories = [...categories];
    // Find the index of the category by its ID
    const categoryIndex = updatedCategories.findIndex(
      (cat) => cat._id === categoryId
    );
    if (categoryIndex !== -1) {
      // Find the index of the subcategory by its ID
      const subCategoryIndex = updatedCategories[
        categoryIndex
      ].subCategory.findIndex((subCat) => subCat._id === subCategoryId);
      if (subCategoryIndex !== -1) {
        // Update the amount of the subcategory
        updatedCategories[categoryIndex].subCategory[subCategoryIndex].amount =
          parseInt(amount) || 0;
        // Update the state with the new categories array
        setFormData(updatedCategories);
      }
    }
  };

  const handleSubmit = async () => {
    let updatedArr = formData.map((item) => {
      let updatedItem = {
        category: item._id,
        subCategory: item.subCategory.map((sub) => ({
          subCategory: sub._id,
          amount: parseInt(sub.amount),
        })),
      };
      return updatedItem;
    });

    let data = {
      role,
      spendLimit: updatedArr,
    };

    const response = await authAxios()({
      url: `/api/v1/admin/reimbursement/spend-limit`,
      method: "POST",
      data,
    });
    toastMessage(response?.data?.message, "success");
  };

  useEffect(() => {
    let updatedCategories = categories.map((category) => {
      let roSpendLimit = spendLimit.find((obj) => obj.role === role);
      if (roSpendLimit) {
        let updatedSubCategory = category.subCategory.map((subCategory) => {
          let matchedSubCategory = roSpendLimit.spendLimit
            .find((s) => s.category === category._id)
            ?.subCategory.find((sub) => sub.subCategory === subCategory._id);
          if (matchedSubCategory) {
            return { ...subCategory, amount: matchedSubCategory.amount };
          }
          return subCategory;
        });
        return { ...category, subCategory: updatedSubCategory };
      }
      return category;
    });
    setCategories(updatedCategories);
  }, [role]);

  return (
    <>
      <PageHeader
        title="Edit Spend Limit"
        subtitle="Set monthly reimbursement limits by position"
      />

      <SectionCard title="Select Position" sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: { xs: "100%", sm: 400 } }} size="small">
          <InputLabel id="zone-label">User</InputLabel>
          <Select
            labelId="zone-label"
            value={role}
            name="User"
            label="User"
            onChange={(event) => setRole(event.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {userType?.map((item) => (
              <MenuItem key={item} value={item?.value}>
                {item?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </SectionCard>

      {categories && categories?.length > 0 ? (
        <Box component="form">
          {categories?.map((category, idx) => (
            <SectionCard key={category._id} sx={{ mb: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 40,
                    height: 40,
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </Avatar>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {category.categoryName}
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {category?.subCategory.map((subCategory) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={subCategory?.subCategory?._id}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label={subCategory?.subCategory}
                      value={subCategory?.amount || ""}
                      onChange={(e) =>
                        handleAmountChange(
                          category._id,
                          subCategory._id,
                          e.target.value
                        )
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>
          ))}
        </Box>
      ) : (
        <SectionCard sx={{ mb: 3 }}>
          <EmptyState
            title="No categories to configure"
            description="Select a position above to load its reimbursement categories."
          />
        </SectionCard>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ py: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          *limit are set to monthly by default
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

const SpendLimit = () => {
  return (
    <AccessController roles={["RO", "ZO", "ADMIN"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SpendLimit;
